// الإشعارات المرسلة من الخادم.
//
// تمييز مهم: تنبيهات الأذان لا تمرّ من هنا. تُجدول محلياً على الجهاز
// (انظر prayerNotifications.js) فتعمل بلا إنترنت وبدقة الثانية، ولا
// تعتمد على خادم قد يتأخر أو ينقطع. هذه للإعلانات وبدء المجالس والردود
// على الفتاوى — ما لا يمكن للجهاز أن يعرفه وحده.

import { API_BASE, isApiConfigured } from '../api/client';

const isNative = () =>
  typeof window !== 'undefined' && Boolean(window.Capacitor?.isNativePlatform?.());

const plugin = () => window.Capacitor?.Plugins?.PushNotifications ?? null;

let registeredToken = null;

/**
 * قناة الإعلانات.
 *
 * أندرويد ٨ فأحدث يُسقط الإشعار **بصمت** إن كانت قناته غير موجودة:
 * الخادم يردّ بنجاح ولا يظهر شيء على الجهاز. فالقناة تُنشأ هنا قبل
 * أي تسجيل.
 *
 * وفصلها عن قناة الأذان مقصود: من أراد تنبيه الصلاة دون إعلانات
 * المجالس يكتم هذه وحدها من إعدادات النظام، بلا أن يفقد الأذان.
 */
const ANNOUNCEMENTS_CHANNEL = {
  id: 'moslam_general',
  name: 'إعلانات المجالس',
  description: 'بدء مجلس الطيبين، والردّ على فتواك، وإعلانات الإذاعة',
  importance: 4, // يظهر فوق الشاشة لحظة وصوله
  visibility: 1, // يُقرأ على شاشة القفل
  vibration: true,
  lights: true,
};

async function ensureChannel(Push) {
  try {
    await Push.createChannel(ANNOUNCEMENTS_CHANNEL);
  } catch {
    // iOS لا قنوات فيه، وإعادة الإنشاء على أندرويد بلا ضرر
  }
}

async function sendToken(token, platform) {
  if (!isApiConfigured()) return;
  try {
    await fetch(`${API_BASE}/api/push/register`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, platform }),
    });
    registeredToken = token;
  } catch {
    // فشل التسجيل لا يعطّل التطبيق؛ يُعاد عند الإقلاع القادم
  }
}

/**
 * يسجّل الجهاز لاستقبال الإشعارات.
 *
 * لا يطلب الإذن إن كان مرفوضاً: نظام التشغيل يتجاهل الطلب المتكرر،
 * وإزعاج المستخدم بنافذة لن تظهر بلا فائدة.
 *
 * @param {(notification: object) => void} onReceived
 */
export async function setupPush({ onReceived } = {}) {
  if (!isNative() || !isApiConfigured()) return;
  // الآيفون يعطي رمز APNs لا رمز FCM، وخادمنا يرسل عبر FCM. فلا تسجيل
  // هناك حتى يُربط Firebase بمفتاح APNs؛ تنبيهات الأذان محلية لا تتأثر.
  if (window.Capacitor?.getPlatform?.() === 'ios') return;

  const Push = plugin();
  if (!Push) return;

  try {
    let status = await Push.checkPermissions();
    if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
      status = await Push.requestPermissions();
    }
    if (status.receive !== 'granted') return;

    Push.addListener('registration', (t) => sendToken(t.value, 'android'));

    Push.addListener('registrationError', (err) =>
      console.warn('تعذر تسجيل الجهاز للإشعارات:', err)
    );

    // إشعار وصل والتطبيق مفتوح: النظام لا يعرضه، فنمرّره للواجهة
    Push.addListener('pushNotificationReceived', (n) => onReceived?.(n));

    // المستخدم ضغط الإشعار: قد نفتح شاشة بعينها لاحقاً
    Push.addListener('pushNotificationActionPerformed', (a) => onReceived?.(a.notification));

    await ensureChannel(Push);
    await Push.register();
  } catch (err) {
    console.warn('تعذر تهيئة الإشعارات:', err);
  }
}

/** يلغي تسجيل الجهاز — يُستدعى عند تسجيل الخروج أو حذف الحساب. */
export async function teardownPush() {
  if (!registeredToken || !isApiConfigured()) return;
  try {
    await fetch(`${API_BASE}/api/push/unregister`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: registeredToken }),
    });
  } catch {
    /* تجاهل */
  }
  registeredToken = null;
}
